import { useLayoutEffect, useRef } from "react";

// Vmemoo-Web verification bridge — BRANDED HANDOFF ONLY.
// This page never validates, consumes or verifies a token. It only reads the
// one-time token from the URL fragment, scrubs it from the visible URL/history,
// and hands it off to the existing native deep link so the signed-in Vmemoo app
// can perform the authoritative authenticated completion.
const TOKEN_SHAPE = /^[A-Za-z0-9_-]{16,256}$/;
const NATIVE_DEEP_LINK_SCHEME = "vmemoo";
const NATIVE_DEEP_LINK_ROUTE = "verification-confirm";

function readTokenFromFragment() {
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;
  const params = new URLSearchParams(hash.slice(1));
  const token = params.get("token");
  // Minimal presence/shape check only — this is NOT backend verification and
  // must never reproduce the mobile backend's real validation rules.
  return token && TOKEN_SHAPE.test(token) ? token : null;
}

// Captured exactly once per page load, at module-evaluation time — i.e. on
// the fresh top-level document load that always occurs when a user opens
// the confirmation link from an email client. This is deliberately NOT read
// inside the component's render/state-initializer, because React 18
// StrictMode intentionally remounts components in development (mount →
// unmount → mount) to surface effect-cleanup bugs; a second read at that
// point would find the fragment already scrubbed by the first mount's
// history.replaceState and incorrectly report "no token". Capturing once at
// module scope is immune to that remount and matches how this page is
// actually ever reached (always a fresh page load, never a client-side SPA
// navigation carrying a fragment).
let capturedToken;
let hasCaptured = false;

function captureTokenOnce() {
  if (!hasCaptured) {
    capturedToken = typeof window !== "undefined" ? readTokenFromFragment() : null;
    hasCaptured = true;
  }
  return capturedToken;
}

// Test-only escape hatch: allows a test suite to simulate a fresh page load
// (a new capture) without resetting the whole module registry, which would
// otherwise create a second, inconsistent copy of React and break hooks.
// Never called by production code.
export function __resetVerifyTokenCaptureForTests() {
  hasCaptured = false;
}

export function VerifyEmailPage() {
  const tokenRef = useRef(captureTokenOnce());
  const token = tokenRef.current;

  useLayoutEffect(() => {
    document.title = "Confirm your email — Vmemoo";
    // Scrub the fragment from the visible URL/history immediately, before paint.
    window.history.replaceState(null, "", "/verify");
    // Route-scoped no-referrer control (document-level, not a genuine HTTP
    // response header — see implementation report). Removed on unmount so it
    // never affects any other route on this site.
    const meta = document.createElement("meta");
    meta.setAttribute("name", "referrer");
    meta.setAttribute("content", "no-referrer");
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const handleContinue = () => {
    const currentToken = tokenRef.current;
    if (!currentToken) return;
    // The token-bearing URI is constructed only here, at the moment of the
    // user's tap — never placed into a static, initial-render anchor href.
    window.location.href = `${NATIVE_DEEP_LINK_SCHEME}://${NATIVE_DEEP_LINK_ROUTE}?token=${encodeURIComponent(currentToken)}`;
  };

  return (
    <main className="verify-page" data-testid="verify-email-page">
      <div className="verify-card" data-testid="verify-email-card">
        <p className="eyebrow eyebrow-amber">Vmemoo · Email confirmation</p>
        {token ? (
          <div data-testid="verify-state-valid">
            <h1>Confirm your Vmemoo email</h1>
            <p className="verify-body">Continue in Vmemoo to securely complete your email confirmation.</p>
            <button type="button" className="button button-accent verify-cta" onClick={handleContinue} data-testid="verify-continue-button">
              Continue in Vmemoo
            </button>
            <p className="verify-security-note">For your security, email confirmation is completed inside the Vmemoo app.</p>
          </div>
        ) : (
          <div data-testid="verify-state-invalid">
            <h1>This verification link is no longer valid</h1>
            <p className="verify-body">Return to Vmemoo and request a new verification email.</p>
            <a className="button button-outline-light verify-cta" href="/" data-testid="verify-back-home-link">Back to Vmemoo</a>
          </div>
        )}
      </div>
    </main>
  );
}
